import * as borogove from "borogove";
import DOMPurify from "dompurify";
import { Button, Message } from "@chatscope/chat-ui-kit-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router";
import { ReactNode, useRef, useLayoutEffect, useState } from "react";
import hljs from "highlight.js";
import {
	Dialog,
	DialogContent,
	List,
	ListItem,
	ListItemText,
} from "@mui/material";
import {
	faCheck,
	faCheckDouble,
	faCircleExclamation,
	faClock,
	faLock,
	faPencil,
	faReply,
} from "@fortawesome/free-solid-svg-icons";

function shortDateTimeOnlyToday(date: Date) {
	const today =
		new Date().toLocaleString([], { dateStyle: "short" }) ===
		date.toLocaleString([], { dateStyle: "short" });
	return date.toLocaleString([], {
		dateStyle: today ? undefined : "short",
		timeStyle: "short",
	});
}

const renderReaction = (reaction: borogove.Reaction) => {
	return reaction.render<JSX.Element | string>(
		(text: string) => text,
		(alt: string, uri: string) => (
			<img crossOrigin="anonymous" alt={alt} src={uri} />
		),
	);
};

const AttachmentContent = ({
	client,
	chat,
	message,
	attachment,
	size,
}: {
	client: borogove.Client;
	chat: borogove.Chat;
	message: borogove.ChatMessage;
	attachment: borogove.ChatAttachment;
	size?: string;
}) => {
	const isPublicChannel = chat instanceof borogove.Channel && !chat.isPrivate();
	const http = attachment.uris.find((uri) => uri.startsWith("http"));
	const [uri, setUri] = useState(() => {
		if (attachment.cachedAt) return attachment.cachedAt;
		if (http && isPublicChannel && chat.isTrusted()) {
			const domain = chat.chatId.split(/[@/]/)[1];
			if (new URL(http).hostname.endsWith(domain)) return http;
		}
		return null;
	});

	if (uri) {
		if (attachment?.mime?.indexOf("image/") === 0) {
			return (
				<a href={uri}>
					<img
						alt={attachment.name || "attachment"}
						crossOrigin="anonymous"
						src={uri}
						style={{ height: size || "15em", objectFit: "scale-down" }}
					/>
				</a>
			);
		} else if (attachment?.mime?.indexOf("audio/") === 0) {
			return <audio crossOrigin="anonymous" src={uri} controls></audio>;
		} else if (attachment?.mime?.indexOf("video/") === 0) {
			return (
				<video
					crossOrigin="anonymous"
					src={uri}
					style={{ height: size || "15em", objectFit: "scale-down" }}
					controls
				></video>
			);
		} else {
			return <a href={uri}>{attachment.name || "attachment"}</a>;
		}
	} else if (isPublicChannel && http) {
		// We don't want to store public channel if we can help it
		return <a href={http}>{attachment.name || "attachment"}</a>;
	} else {
		return (
			<Button
				onClick={async () => {
					const filledIn = await message.fetchAttachment(attachment, client);
					setUri(filledIn.cachedAt);
				}}
			>
				Fetch Attachment
			</Button>
		);
	}
};

const linkMeta = (meta: borogove.LinkMetadata) => {
	const image = meta.image.find((image) => image.match(/^(data|https):/));

	return (
		<a
			className="h-item"
			href={meta.url}
			title={"Description of " + meta.about}
		>
			{image && <img className="u-photo" src={image} alt="Image of link" />}
			{meta.title && <div className="p-name">{meta.title}</div>}
			<div className="u-url">{meta.url}</div>
			{meta.description && <div className="p-summary">{meta.description}</div>}
		</a>
	);
};

const formatMention = (dom: Node, name: string) => {
	const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	const regex = new RegExp(`(\\b${escapeRegExp(name)}\\b)`, "g");

	const walker = document.createTreeWalker(dom, NodeFilter.SHOW_TEXT, {
		acceptNode: (node) => {
			if (regex.test(node?.nodeValue ?? "")) {
				return NodeFilter.FILTER_ACCEPT;
			}
			return NodeFilter.FILTER_REJECT;
		},
	});

	while (walker.nextNode()) {
		const textNode = walker.currentNode;
		if (!textNode?.nodeValue) continue;

		const parts = textNode.nodeValue.split(regex);

		const fragment = document.createDocumentFragment();
		for (const [index, part] of parts.entries()) {
			if (!part) continue;

			// Odd indices are the matched names (due to capture group)
			if (index % 2 === 1) {
				const span = document.createElement("span");
				span.className = "self";
				span.textContent = part;
				fragment.appendChild(span);
			} else {
				fragment.appendChild(document.createTextNode(part));
			}
		}

		textNode.parentNode?.replaceChild(fragment, textNode);
	}

	return dom;
};

const MessageContent = ({
	m,
	selfName,
	sender,
}: {
	m: borogove.ChatMessage;
	selfName: string;
	sender: borogove.Member | undefined;
}) => {
	const contentRef = useRef<HTMLDivElement | null>(null);
	const senderRef = useRef(sender);

	useLayoutEffect(() => {
		if (!contentRef.current) return;

		const html = m.body(senderRef.current).asDOM();
		DOMPurify.sanitize(formatMention(html, selfName));

		contentRef.current.replaceChildren(html);
		for (const el of contentRef.current.querySelectorAll<HTMLElement>(
			"pre > code[class^=language-]",
		)) {
			el.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
			hljs.highlightElement(el);
		}
	}, [m, selfName]);

	return (
		<>
			{!m.moderationReason() &&
				m.encryption?.status !==
					borogove.EncryptionStatus.DecryptionFailure && (
					<div className="cs-message__html-content" ref={contentRef} />
				)}
			{m.encryption &&
				m.encryption?.status ===
					borogove.EncryptionStatus.DecryptionFailure && (
					<div
						className="decryption-failure"
						title={m.encryption.methodName ?? m.encryption.method}
					>
						<FontAwesomeIcon icon={faCircleExclamation} />
						{" " + (m.encryption.reasonText ?? m.encryption.reason)}
					</div>
				)}
			{m.moderationReason() && (
				<div className="moderated">{m.moderationReason()}</div>
			)}
		</>
	);
};

const EditHistory = ({
	open,
	onClose,
	versions,
	selfName,
	sender,
}: {
	open: boolean;
	onClose: () => void;
	versions: borogove.ChatMessage[];
	selfName: string;
	sender: borogove.Member | undefined;
}) => {
	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent>
				<h1>Edit History</h1>
				<List>
					{versions.map((version, idx) => (
						<ListItem divider={idx < versions.length - 1} key={version.localId}>
							<ListItemText
								primary={
									<MessageContent
										m={version}
										selfName={selfName}
										sender={sender}
									/>
								}
								secondary={
									(idx === 0 ? "Current Version @ " : "") +
									shortDateTimeOnlyToday(new Date(version.timestamp))
								}
							/>
						</ListItem>
					))}
				</List>
			</DialogContent>
		</Dialog>
	);
};

export const MessageBubble = ({
	chat,
	client,
	members,
	m,
	position,
	setSelectedThread,
	className,
	children,
}: {
	chat: borogove.Chat;
	client: borogove.Client;
	members: Map<string, borogove.Member>;
	m: borogove.ChatMessage;
	position: "first" | "last" | "single" | "normal";
	setSelectedThread: (id: string | null) => void;
	className?: string;
	children: ReactNode;
}) => {
	const navigate = useNavigate();

	const toggleReaction = (
		m: borogove.ChatMessage,
		reaction: borogove.Reaction,
	) => {
		const myReaction = (m.reactions.get(reaction.key) || []).find(
			(r: borogove.Reaction) => members.get(r.senderId)?.isSelf,
		);
		if (myReaction) {
			chat.removeReaction(m, myReaction);
		} else {
			chat.addReaction(m, reaction);
		}
	};

	const [showEdits, setShowEdits] = useState(false);
	const bodyLength = m
		.body()
		.reduce((txt, isTag, _, kids: number[] | null) =>
			isTag ? (kids ?? []).reduce((x, y) => x + y, 0) : txt.length,
		)
		.reduce((x, y) => x + y, 0);
	const parentMessage = m.replyToMessage;
	const reactionEntries = [...(m.reactions.entries() || [])];
	const shortDateTimeHuman = shortDateTimeOnlyToday(new Date(m.timestamp));
	const fullDateTimeHuman = new Date(m.timestamp)
		.toLocaleString([], {
			dateStyle: "full",
			timeStyle: "long",
		})
		.replace(/\s*\b[A-Z]{2,4}$/, ""); // remove trailing TZ abbrev

	const sender = members.get(m.senderId);

	return (
		<Message
			model={{
				direction: m.isIncoming() ? "incoming" : "outgoing",
				position,
			}}
			onClick={() => setSelectedThread(m.threadId)}
			id={`message:${m.serverId || m.localId}`}
			data-server-id={m.serverId}
			data-local-id={m.localId}
			className={
				(bodyLength < 25 && !m.replyToMessage ? "small " : "") +
				(m.type === borogove.MessageType.MessageChannelPrivate
					? "invite"
					: "") +
				" " +
				className
			}
		>
			{children}
			<Message.CustomContent>
				<div title={fullDateTimeHuman}>
					{parentMessage && (
						<>
							<Button
								title="Jump to parent"
								icon={<FontAwesomeIcon icon={faReply} />}
								onClick={() => navigate(`#message:${parentMessage.serverId}`)}
							>
								{members.get(parentMessage.senderId)?.displayName}
							</Button>
							<blockquote className="u-in-reply-to">
								<MessageContent
									m={parentMessage}
									selfName={client.displayName()}
									sender={members.get(parentMessage.senderId)}
								/>
								{parentMessage.attachments.length > 0 && (
									<p>
										{parentMessage.attachments.map((attachment, idx) => (
											<AttachmentContent
												key={idx}
												client={client}
												chat={chat}
												message={m}
												attachment={attachment}
											/>
										))}
									</p>
								)}
							</blockquote>
						</>
					)}
					<MessageContent
						m={m}
						selfName={client.displayName()}
						sender={sender}
					/>
					{m.attachments.length > 0 && (
						<p>
							{m.attachments.map((attachment, idx) => (
								<AttachmentContent
									key={idx}
									client={client}
									chat={chat}
									message={m}
									attachment={attachment}
								/>
							))}
						</p>
					)}
					{m.linkMetadata.map((meta) => linkMeta(meta))}
					<div className="meta">
						<div
							className={
								(m.senderId !== chat.chatId &&
								["last", "single"].includes(position) &&
								!m.moderationReason() &&
								m.isIncoming()
									? ""
									: "sr-only") + " h-card sender"
							}
						>
							{sender?.displayName}
						</div>

						<div>
							{m.subject() && (
								<>
									<strong>{m.subject()}</strong>{" "}
								</>
							)}
							{m.timestamp && ["last", "single"].includes(position) && (
								<time dateTime={m.timestamp} title={fullDateTimeHuman}>
									{shortDateTimeHuman}
								</time>
							)}
							{m.versions.length > 0 && (
								<FontAwesomeIcon
									icon={faPencil}
									title={"Edited " + (m.versions.length - 1) + " times"}
									onClick={() => setShowEdits(true)}
								/>
							)}
							{m.threadId && (
								<img
									src={m.threadIcon() ?? undefined}
									alt={"thread " + m.threadId.substring(0, 5)}
									className="thread"
								/>
							)}
							{m.encryption &&
								m.encryption?.status ===
									borogove.EncryptionStatus.DecryptionSuccess && (
									<FontAwesomeIcon
										icon={faLock}
										title={m.encryption.methodName ?? m.encryption.method}
									/>
								)}
							{!m.isIncoming() &&
								m.status === borogove.MessageStatus.MessagePending && (
									<FontAwesomeIcon
										icon={faClock}
										title={m.statusText ?? "Sending..."}
									/>
								)}
							{!m.isIncoming() &&
								m.status === borogove.MessageStatus.MessageFailedToSend && (
									<FontAwesomeIcon
										icon={faCircleExclamation}
										title={m.statusText ?? "Error"}
									/>
								)}
							{!m.isIncoming() &&
								m.status ===
									borogove.MessageStatus.MessageDeliveredToServer && (
									<FontAwesomeIcon
										icon={faCheck}
										title={m.statusText ?? "Sent"}
									/>
								)}
							{!m.isIncoming() &&
								m.status ===
									borogove.MessageStatus.MessageDeliveredToDevice && (
									<FontAwesomeIcon
										icon={faCheckDouble}
										title={m.statusText ?? "Received"}
									/>
								)}
						</div>
					</div>
				</div>
				<EditHistory
					open={showEdits}
					onClose={() => setShowEdits(false)}
					versions={m.versions}
					selfName={client.displayName()}
					sender={sender}
				/>
			</Message.CustomContent>
			{reactionEntries.length > 0 && (
				<Message.Footer className="reactions">
					{reactionEntries.map(([key, reactions]) => (
						<Button
							border
							onClick={() =>
								toggleReaction(
									m,
									reactions.find((r) => members.get(r.senderId)?.isSelf) ||
										reactions[0],
								)
							}
							key={key}
							className={
								reactions.find((r) => members.get(r.senderId)?.isSelf)
									? "reaction mine"
									: "reaction"
							}
							title={reactions
								.map(
									(r) =>
										members.get(r.senderId)?.displayName +
										" @ " +
										shortDateTimeOnlyToday(new Date(r.timestamp)),
								)
								.join("\n")}
						>
							{renderReaction(reactions[0])} {" " + reactions.length}
						</Button>
					))}
				</Message.Footer>
			)}
		</Message>
	);
};
